import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Box, Mail } from "lucide-react";
import { useContent, useVisibleSections } from "@/content/ContentContext";
import { TYPE_VIEWS } from "@/components/hud/SectionViews";
import { Lightbox } from "@/components/hud/Lightbox";
import { Logo } from "@/components/hud/Logo";
import { LangToggle } from "@/components/hud/LangToggle";
import { CvDownload } from "@/components/hud/Extras";
import { useT } from "@/content/i18n";

const ease = [0.22, 1, 0.36, 1];

export default function Classic() {
  const { profile } = useContent();
  const sections = useVisibleSections();
  const t = useT();
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = (items, index) => setLightbox({ items, index });
  const moveLightbox = (d) => setLightbox((s) => s && { ...s, index: (s.index + d + s.items.length) % s.items.length });
  const jump = (id) => document.getElementById(`cv-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const contactId = sections.find((s) => s.type === "contact")?.id;
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    const els = sections.map((s) => document.getElementById(`cv-${s.id}`)).filter(Boolean);
    const visible = new Set();
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? visible.add(e.target.id) : visible.delete(e.target.id)));
      const first = els.find((el) => visible.has(el.id));
      if (first) setCurrent(first.id.slice(3));
    }, { rootMargin: "-35% 0px -55% 0px" });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [sections]);

  return (
    <div className="min-h-screen bg-[#12100E]" data-testid="classic-page">
      <header className="sticky top-0 z-30 border-b border-[#F7F4EE]/10 bg-[#12100E]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-5 py-3">
          <Link to="/" className="flex items-center gap-3" data-testid="classic-home-link">
            <Logo className="h-9 w-9" />
            <span className="hidden font-display text-base text-[#F7F4EE] sm:block">{profile.firstName} {profile.lastName}</span>
          </Link>
          <div className="flex items-center gap-2">
            <LangToggle />
            <CvDownload className="chip chip-gold" testId="classic-cv-download" /><Link to="/" className="chip" data-testid="classic-enter-room" aria-label={t("enterRoom")}><Box className="h-4 w-4" /><span className="hidden sm:inline">{t("enterRoom")}</span></Link>
          </div>
        </div>
        <nav className="mx-auto flex max-w-5xl flex-wrap gap-1 px-5 pb-3" data-testid="classic-nav">
          {sections.map((s) => (
            <button key={s.id} onClick={() => jump(s.id)} data-testid={`classic-nav-${s.id}`}
              className={`rounded-full px-2.5 py-1 text-xs transition-colors sm:px-3 sm:py-1.5 ${current === s.id ? "bg-[#E69C24] text-[#12100E]" : "text-[#D9CFC2] hover:bg-[#F7F4EE]/8 hover:text-[#F7F4EE]"}`}>{s.label}</button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-5xl px-5 pb-24">
        <section className="py-16 sm:py-24">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E69C24]">{t("tagline").split("— ")[1]}</p>
          <h1 className="mt-4 font-display text-4xl leading-tight text-[#F7F4EE] sm:text-5xl lg:text-6xl">{profile.firstName} {profile.lastName}</h1>
          <p className="mt-3 text-base text-[#E6B877] md:text-lg">{profile.role}</p>
          {profile.tagline && <p className="mt-6 max-w-2xl text-sm leading-relaxed text-[#C5BDB3] sm:text-base">{profile.tagline}</p>}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button onClick={() => jump(sections[0]?.id)} className="btn-gold" data-testid="classic-start">{t("browse")} <ArrowRight className="h-4 w-4" /></button>
            <CvDownload className="cv-cta !animate-none !py-3" testId="classic-hero-cv-download" compact={false} />
            {contactId && (
              <button onClick={() => jump(contactId)} className="chip !px-5 !py-3 !text-sm" data-testid="classic-contact-button">
                <Mail className="h-4 w-4" />{t("contactMe")}
              </button>
            )}
          </div>
        </section>

        {sections.map((s, i) => {
          const View = TYPE_VIEWS[s.type];
          return (
            <motion.section
              key={s.id}
              id={`cv-${s.id}`}
              data-testid={`classic-section-${s.id}`}
              className="scroll-mt-48 border-t border-[#F7F4EE]/10 py-12 md:grid md:grid-cols-[220px_1fr] md:gap-10"
              initial={{ opacity: 0.4, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.7, ease }}
            >
              <div className="mb-6 md:mb-0">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#E69C24]">N° {String(i + 1).padStart(2, "0")}</p>
                <h2 className="mt-2 font-display text-2xl text-[#F7F4EE]">{s.label}</h2>
              </div>
              <div className="min-w-0">{View ? <View data={s.data} openLightbox={openLightbox} /> : null}</div>
            </motion.section>
          );
        })}
      </main>
      <Lightbox state={lightbox} onClose={() => setLightbox(null)} onMove={moveLightbox} />
    </div>
  );
}
